<!--
Copyright (c) 2025-2026, NVIDIA CORPORATION.  All rights reserved.

NVIDIA CORPORATION and its licensors retain all intellectual property
and proprietary rights in and to this software, related documentation
and any modifications thereto.  Any use, reproduction, disclosure or
distribution of this software and related documentation without an express
license agreement from NVIDIA CORPORATION is strictly prohibited.
-->

<div
    role="table"
    class="hig-generic-table"
    [attr.aria-label]="ariaLabel"
    [attr.aria-colcount]="columns?.length || 0"
    [attr.aria-rowcount]="rowCount + 1"
>
    <!-- Existing column-major visual DOM -->
    @for (column of columns; let i = $index; track i) {
        <div class="hig-generic-table-column">
            <!-- column header -->
            <div class="hig-generic-table-header">
                <span
                    dir="auto"
                    role="columnheader"
                    [attr.id]="colHeaderIds[i]"
                    [attr.aria-colindex]="i + 1"
                    class="hig-generic-table-content-text-header"
                >
                    {{ column.title }}
                </span>
            </div>

            <!-- data cells -->
            @for (cell of column.cells; let j = $index; track j) {
                <div
                    role="cell"
                    class="hig-generic-table-content"
                    [attr.id]="cellIdsByColumn[i][j]"
                    [attr.aria-colindex]="i + 1"
                    [attr.aria-labelledby]="colHeaderIds[i]"
                >
                    @if (cell.iconName) {
                        <mat-icon
                            class="hig-generic-table-icon"
                            [class.warning]="cell.warning"
                            [svgIcon]="cell.iconName"
                        >
                        </mat-icon>
                    }
                    <span
                        dir="auto"
                        [class.hig-generic-table-content-text]="!cell.iconName"
                        [class.hig-generic-table-content-text-icon]="cell.iconName"
                        >{{ cell.description?.text }}</span
                    >
                </div>
            }

            <!-- CTA row -->
            @if (!!column.cta) {
                <div
                    role="cell"
                    [attr.id]="ctaCellIds[i]"
                    [attr.aria-colindex]="i + 1"
                    [attr.aria-labelledby]="colHeaderIds[i]"
                    class="hig-generic-table-content"
                >
                    @if (!column.cta.showStringInsteadOfCTA) {
                        <button
                            mat-button
                            [nvFocusRipple]="column.cta.accentColor"
                            [nvAutoFocus]="!column.cta.accentColor"
                            class="hig-generic-table-button font-button2"
                            [class.mat-accent]="column.cta.accentColor"
                            (click)="onButtonClick(column, i)"
                        >
                            <span class="">{{ column.cta.text | translate }}</span>
                            <mat-icon
                                *ngIf="column.cta.svgIcon"
                                iconPositionEnd
                                class="hig-generic-table-button-icon"
                                [svgIcon]="column.cta.svgIcon"
                                aria-hidden="true"
                            ></mat-icon>
                        </button>
                    }
                    @if (!!column.cta.showStringInsteadOfCTA) {
                        <span
                            dir="auto"
                            [class.hig-generic-table-content-text]="!!column.cta.showStringInsteadOfCTA"
                            [class.hig-generic-table-content-text-italics]="!!column.cta.showStringInsteadOfCTA"
                            >{{ column.cta.text | translate }}</span
                        >
                    }
                </div>
            }
        </div>
    }
    <!-- hidden proxy rows to satisfy ARIA hierarchy -->
    <div class="cdk-visually-hidden" role="row" aria-rowindex="1" [attr.aria-owns]="headerRowOwns"></div>

    @for (r of rowIndexes; let rIdx = $index; track rIdx) {
        <div
            class="cdk-visually-hidden"
            role="row"
            [attr.aria-rowindex]="rIdx + 2"
            [attr.aria-owns]="rowOwnsByIndex[rIdx]"
        ></div>
    }
</div>
